import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import PulseLoader from "@/components/PulseLoader";
import {
  BsCalendar3,
  BsChevronDown,
  BsChevronUp,
  BsClock,
  BsShieldCheck,
  BsShuffle,
  BsTrash,
  BsX,
} from "react-icons/bs";
import { FaPlay, FaUsers } from "react-icons/fa";
import { MdOutlineFactCheck } from "react-icons/md";
import { LuPlus } from "react-icons/lu";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import authService from "../../../utils/authService";
import ConfirmationModal from "../../../components/ConfirmationModal";
import {
  isPracticeAttendanceDecisionLocked,
  practiceWindowAllowsDuration,
  slotAttendanceUiMode,
} from "../../../constants/testConstants";
import type { TrainerLiveState } from "../../../attendance/types/attendance";
import { TEST_FLOW_API } from "./testApiPaths";
import type { BatchConfigEntry } from "./testTypes";
import QuestionPreviewModal from "./preview/QuestionPreviewModal";
import type { PreviewQuestion } from "./preview/questionPreviewAdapter";
import { DEFAULT_TEST_SECURITY_CONFIGURATION } from "./testUtils";
import { decodeTestNameFromPath, useTestRoutes } from "./testRoutes";

type SlotBatch = {
  batch_id: string;
  batch_name?: string | null;
  course_id?: string | null;
  course_name?: string | null;
  assigned_student_count?: number;
};

type TestSlot = {
  slot_id: number;
  test_id: string;
  slot_index: number;
  label: string;
  start_time?: string | null;
  end_time?: string | null;
  duration_minutes: number;
  auto_release_test_report: boolean;
  report_released: boolean;
  status: string;
  question_count: number;
  batches: SlotBatch[];
  batch_assigned_counts?: Record<string, number>;
  attendance_session_id?: string | null;
  attendance_launched?: boolean;
  attendance_round_completed?: boolean;
  consider_attendance?: boolean;
  consider_duration?: boolean;
  assigned_student_count?: number;
  incomplete_student_count?: number;
  can_manual_release?: boolean;
  manual_release_block_reason?: string | null;
  manual_release_allow_reason?: string | null;
};

type SameTestSlotUsage = {
  slot_id: number;
  slot_label: string;
};

type UsedInTestUsage = {
  test_id: string;
  test_name: string;
  used_at?: string | null;
};

type SlotQuestion = {
  question_id: string;
  sort_order: number;
  is_reused: boolean;
  question_type?: string;
  question_types_enum?: string | null;
  level?: string;
  subject_id?: string;
  topic_id?: string;
  sub_topic_id?: string;
  subject_name?: string;
  topic_name?: string;
  sub_topic_name?: string;
  swap_available?: number;
  question_data?: Record<string, unknown> | null;
  same_test_slots?: SameTestSlotUsage[];
  used_in_tests?: UsedInTestUsage[];
};

export type TestSlotsPanelHandle = {
  /** Attach batch to sole slot, or to the currently open slot when multiple exist. */
  attachBatch: (batchId: string) => Promise<string | null>;
};

export type PaperTotals = {
  duration: number;
  marks: number;
};

type TestSlotsPanelProps = {
  testId: string;
  testName?: string;
  testType?: string | null;
  logicSaved?: boolean;
  batchConfigs: BatchConfigEntry[];
  onAssignStudents: (batch: BatchConfigEntry, slotId?: number) => void;
  onRemoveBatch: (batchId: string) => void | Promise<void>;
  onSlotsChanged?: (totals?: PaperTotals) => void;
};

const toDateLabel = (iso?: string | null) => {
  if (!iso) return "Not Set";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Not Set";
  return d.toLocaleDateString();
};

const toTimeLabel = (iso?: string | null) => {
  if (!iso) return "Not Set";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Not Set";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const toLocalInput = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value: string) => {
  if (!value) return null;
  return new Date(value).toISOString();
};

const SLOT_DELETE_LOCKED_TITLE = "Cannot delete live/Completed test";

/** Live, completed, or past start_time — same window lock as attendance. */
const getSlotBatchLockReason = (slot: TestSlot): string | null => {
  if (!isPracticeAttendanceDecisionLocked(slot)) return null;
  return SLOT_DELETE_LOCKED_TITLE;
};

const getSlotDeleteLockReason = (slot: TestSlot): string | null => {
  if (!isPracticeAttendanceDecisionLocked(slot)) return null;
  return SLOT_DELETE_LOCKED_TITLE;
};

/** Add minutes to a datetime-local value; returns datetime-local string. */
const addMinutesToLocalInput = (value: string, minutes: number) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes() + Math.max(0, minutes));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const questionText = (q: SlotQuestion, maxLength = 220) => {
  const data = q.question_data;
  if (!data || typeof data !== "object") return q.question_id;
  const text = String(data.Qn || data.question || data.text || q.question_id);
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
};

const formatUsedAt = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const usedHoverLines = (tests: UsedInTestUsage[] = []) =>
  tests
    .map((t, i) => {
      const when = formatUsedAt(t.used_at);
      return when
        ? `${i + 1}. ${t.test_name} - ${when}`
        : `${i + 1}. ${t.test_name}`;
    })
    .join("\n");

const QuestionUsedBadge: React.FC<{ question: SlotQuestion }> = ({
  question,
}) => {
  const usedTests = question.used_in_tests || [];
  if (usedTests.length === 0) return null;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`used-tip-${question.question_id}`}>
          <div style={{ whiteSpace: "pre-line", textAlign: "left" }}>
            {usedHoverLines(usedTests)}
          </div>
        </Tooltip>
      }
    >
      <Badge bg="warning" text="dark" style={{ cursor: "help" }}>
        Used
      </Badge>
    </OverlayTrigger>
  );
};

const TestSlotsPanel = forwardRef<TestSlotsPanelHandle, TestSlotsPanelProps>(
  (
    {
      testId,
      testName,
      testType,
      logicSaved = false,
      batchConfigs,
      onAssignStudents,
      onRemoveBatch,
      onSlotsChanged,
    },
    ref
  ) => {
  const navigate = useNavigate();
  const routes = useTestRoutes();
  const [searchParams, setSearchParams] = useSearchParams();
  const { testName: testNameParam } = useParams<{ testName: string }>();
  const routeTestName = decodeTestNameFromPath(testNameParam) || testName || "";
  const attendanceUi = slotAttendanceUiMode(testType);
  const showAttendanceStart = attendanceUi === "final";
  const showPracticeAttendanceChoice = attendanceUi === "practice";
  const showAttendanceButton =
    showAttendanceStart || showPracticeAttendanceChoice;
  const reopenHandledRef = useRef(false);
  const [slots, setSlots] = useState<TestSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busySlotId, setBusySlotId] = useState<number | null>(null);
  const [expandedSlotId, setExpandedSlotId] = useState<number | null>(null);
  const [scheduleSlotId, setScheduleSlotId] = useState<number | null>(null);
  const [attendanceChoiceSlotId, setAttendanceChoiceSlotId] = useState<
    number | null
  >(null);
  const [securitySlotId, setSecuritySlotId] = useState<number | null>(null);
  const [reviewSlotId, setReviewSlotId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<SlotQuestion[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [swapNotice, setSwapNotice] = useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [swappingQuestionId, setSwappingQuestionId] = useState<string | null>(
    null
  );
  const [swappingAll, setSwappingAll] = useState(false);
  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");
  const [draftConsiderDuration, setDraftConsiderDuration] = useState(false);
  const [draftAutoRelease, setDraftAutoRelease] = useState(false);
  const [regeneratePromptSlotId, setRegeneratePromptSlotId] = useState<
    number | null
  >(null);
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "slot"; slotId: number; name: string }
    | { kind: "batch"; slotId: number; batchId: string; name: string }
    | null
  >(null);
  const [deleting, setDeleting] = useState(false);
  const [previewQuestion, setPreviewQuestion] =
    useState<PreviewQuestion | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const slotsRef = useRef<TestSlot[]>([]);
  const expandedSlotIdRef = useRef<number | null>(null);
  expandedSlotIdRef.current = expandedSlotId;

  const loadSlots = useCallback(async (): Promise<TestSlot[]> => {
    setLoading(true);
    setError("");
    try {
      const res = await authService.get(
        `${TEST_FLOW_API}/tests/${testId}/slots`
      );
      const next: TestSlot[] = res.data?.slots || [];
      slotsRef.current = next;
      setSlots(next);
      return next;
    } catch (err) {
      console.error(err);
      setError("Failed to load slots.");
      return slotsRef.current;
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  useEffect(() => {
    if (!swapNotice) return;
    const timer = window.setTimeout(() => setSwapNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [swapNotice]);

  useEffect(() => {
    if (!practiceWindowAllowsDuration(draftStart, draftEnd)) {
      setDraftConsiderDuration(false);
    }
  }, [draftStart, draftEnd]);

  const putSlotBatches = async (slotId: number, batchIds: string[]) => {
    setBusySlotId(slotId);
    try {
      await authService.put(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slotId}/batches`,
        { batch_ids: batchIds }
      );
      await loadSlots();
      onSlotsChanged?.();
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to set batches.";
      setError(String(detail));
      throw err;
    } finally {
      setBusySlotId(null);
    }
  };

  useImperativeHandle(ref, () => ({
    attachBatch: async (batchId: string) => {
      setError("");
      let current = slotsRef.current.length
        ? slotsRef.current
        : await loadSlots();

      if (current.length === 0) {
        try {
          const res = await authService.post(
            `${TEST_FLOW_API}/tests/${testId}/slots`
          );
          current = await loadSlots();
          const createdId = res.data?.slot_id as number | undefined;
          if (createdId) setExpandedSlotId(createdId);
          onSlotsChanged?.();
        } catch (err) {
          console.error(err);
          return "Failed to create slot for this batch.";
        }
      }

      if (current.length === 0) {
        return "No slot available. Add a slot first.";
      }

      let targetId: number | null = null;
      if (current.length === 1) {
        targetId = current[0].slot_id;
      } else if (
        expandedSlotIdRef.current != null &&
        current.some((s) => s.slot_id === expandedSlotIdRef.current)
      ) {
        targetId = expandedSlotIdRef.current;
      } else {
        return "Open a slot to add the batch into it.";
      }

      const slot = current.find((s) => s.slot_id === targetId);
      if (!slot) {
        return "Slot not found.";
      }
      const lockReason = getSlotBatchLockReason(slot);
      if (lockReason) {
        // Caller (Add Batch) shows this once — do not also set panel error
        return lockReason;
      }
      if (slot.batches.some((b) => b.batch_id === batchId)) {
        return "This batch is already on the open slot. Expand another slot to add it there.";
      }
      const nextIds = [
        ...new Set([
          ...slot.batches.map((b) => b.batch_id),
          batchId,
        ]),
      ];
      try {
        await putSlotBatches(targetId, nextIds);
        setExpandedSlotId(targetId);
        return null;
      } catch (err: unknown) {
        // putSlotBatches also sets panel error — clear so parent shows once
        setError("");
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail || "Failed to add batch to slot.";
        return String(detail);
      }
    },
  }));

  const createSlot = async () => {
    setError("");
    try {
      const res = await authService.post(
        `${TEST_FLOW_API}/tests/${testId}/slots`
      );
      await loadSlots();
      if (res.data?.slot_id) setExpandedSlotId(res.data.slot_id);
      onSlotsChanged?.();
    } catch (err) {
      console.error(err);
      setError("Failed to create slot.");
    }
  };

  const requestDeleteSlot = (slotId: number, name: string) => {
    setDeleteTarget({ kind: "slot", slotId, name });
  };

  const requestDeleteBatch = (
    slotId: number,
    batchId: string,
    name: string
  ) => {
    setDeleteTarget({ kind: "batch", slotId, batchId, name });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.kind === "batch") {
      setDeleting(true);
      try {
        await removeBatchFromSlot(deleteTarget.slotId, deleteTarget.batchId);
        setDeleteTarget(null);
      } finally {
        setDeleting(false);
      }
      return;
    }

    const { slotId } = deleteTarget;
    setDeleting(true);
    setBusySlotId(slotId);
    setError("");
    try {
      await authService.delete(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slotId}`
      );
      if (expandedSlotId === slotId) setExpandedSlotId(null);
      if (reviewSlotId === slotId) setReviewSlotId(null);
      if (scheduleSlotId === slotId) setScheduleSlotId(null);
      if (securitySlotId === slotId) setSecuritySlotId(null);
      setDeleteTarget(null);
      await loadSlots();
      onSlotsChanged?.();
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to delete slot.";
      setError(String(detail));
    } finally {
      setDeleting(false);
      setBusySlotId(null);
    }
  };

  const patchSlot = async (
    slotId: number,
    body: Record<string, unknown>
  ): Promise<boolean> => {
    setBusySlotId(slotId);
    try {
      await authService.patch(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slotId}`,
        body
      );
      await loadSlots();
      onSlotsChanged?.();
      return true;
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to update slot.";
      setError(String(detail));
      return false;
    } finally {
      setBusySlotId(null);
    }
  };

  const removeBatchFromSlot = async (slotId: number, batchId: string) => {
    const slot = slots.find((s) => s.slot_id === slotId);
    if (!slot) return;
    const lockReason = getSlotBatchLockReason(slot);
    if (lockReason) {
      setError(lockReason);
      return;
    }
    const nextIds = slot.batches
      .map((b) => b.batch_id)
      .filter((id) => id !== batchId);
    try {
      await putSlotBatches(slotId, nextIds);
      // Clear students previously assigned to this slot for this batch
      try {
        await authService.post(`${TEST_FLOW_API}/tests/${testId}/assign`, {
          students_list: [],
          batch_id: batchId,
          slot_id: slotId,
        });
      } catch {
        /* optional cleanup — batch link already removed */
      }
      const refreshed = slotsRef.current;
      const stillLinked = refreshed.some((s) =>
        s.batches.some((b) => b.batch_id === batchId)
      );
      if (!stillLinked) {
        await onRemoveBatch(batchId);
      }
    } catch {
      /* error already set */
    }
  };

  const resolveBatchConfig = (batchId: string): BatchConfigEntry | undefined =>
    batchConfigs.find((b) => String(b.batch_id) === String(batchId));

  const notifyPaperTotals = useCallback(
    (data?: { duration_minutes?: number; marks?: number } | null) => {
      if (
        data &&
        typeof data.duration_minutes === "number" &&
        typeof data.marks === "number"
      ) {
        onSlotsChanged?.({
          duration: data.duration_minutes,
          marks: data.marks,
        });
        return;
      }
      onSlotsChanged?.();
    },
    [onSlotsChanged]
  );

  const generate = async (slotId: number, regenerate: boolean) => {
    if (!logicSaved) {
      setError("Save selection logic at test level first.");
      return;
    }
    setBusySlotId(slotId);
    setError("");
    try {
      const path = regenerate ? "regenerate" : "generate";
      const res = await authService.post(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slotId}/${path}`
      );
      setWarnings(res.data?.warnings || []);
      setQuestions(res.data?.questions || []);
      setReviewSlotId(slotId);
      await loadSlots();
      notifyPaperTotals(res.data);
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to generate questions.";
      setError(String(detail));
    } finally {
      setBusySlotId(null);
    }
  };

  const openReview = useCallback(async (slotId: number) => {
    setReviewSlotId(slotId);
    setReviewLoading(true);
    setWarnings([]);
    try {
      const res = await authService.get(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slotId}/questions`
      );
      setQuestions(res.data?.questions || []);
      notifyPaperTotals(res.data);
    } catch (err) {
      console.error(err);
      setQuestions([]);
    } finally {
      setReviewLoading(false);
    }
  }, [testId, notifyPaperTotals]);

  useEffect(() => {
    if (reopenHandledRef.current) return;
    const reopenSlot = searchParams.get("reopen_slot");
    if (!reopenSlot) return;
    const slotId = Number(reopenSlot);
    if (!Number.isFinite(slotId) || slotId <= 0) return;
    reopenHandledRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete("reopen_slot");
    setSearchParams(next, { replace: true });
    void openReview(slotId);
  }, [searchParams, setSearchParams, openReview]);

  const openSelectReplacement = (question: SlotQuestion) => {
    if (!reviewSlotId || !routeTestName) return;
    const slot = slots.find((s) => s.slot_id === reviewSlotId);
    if (
      slot?.status === "live" ||
      slot?.status === "completed" ||
      (slot?.assigned_student_count || 0) > 0
    ) {
      return;
    }
    navigate(
      routes.replaceQuestion(routeTestName, {
        test_id: testId,
        slot_id: String(reviewSlotId),
        question_id: question.question_id,
        subject_name: question.subject_name,
        topic_name: question.topic_name,
        sub_topic_name: question.sub_topic_name,
      })
    );
  };

  const swap = async (questionId?: string, swapAll = false) => {
    if (!reviewSlotId) return;
    const slot = slots.find((s) => s.slot_id === reviewSlotId);
    if (slot?.status === "live" || slot?.status === "completed") {
      setError("Cannot swap questions after the test has started or completed.");
      return;
    }
    if ((slot?.assigned_student_count || 0) > 0) {
      setError("Cannot swap questions after students are mapped to this slot.");
      return;
    }
    if (swapAll) setSwappingAll(true);
    else if (questionId) setSwappingQuestionId(questionId);
    try {
      const res = await authService.post(
        `${TEST_FLOW_API}/tests/${testId}/slots/${reviewSlotId}/swap`,
        { question_id: questionId || null, swap_all: swapAll }
      );
      setQuestions(res.data?.questions || []);
      setSwapNotice(
        swapAll
          ? "Swapped all eligible questions."
          : questionId
            ? `Swapped ${questionId}`
            : null
      );
      await loadSlots();
      notifyPaperTotals(res.data);
    } catch (err) {
      console.error(err);
      setError("Swap failed.");
    } finally {
      setSwappingQuestionId(null);
      setSwappingAll(false);
    }
  };

  const openSchedule = (slot: TestSlot) => {
    setScheduleSlotId(slot.slot_id);
    const start = toLocalInput(slot.start_time);
    setDraftStart(start);
    let end = toLocalInput(slot.end_time);
    if (showAttendanceStart) {
      // Final / Overall Final: end = start + slot duration (no manual end)
      const mins = slot.duration_minutes || 0;
      end = start
        ? addMinutesToLocalInput(start, mins)
        : toLocalInput(slot.end_time);
      setDraftEnd(end);
    } else {
      setDraftEnd(end);
    }
    setDraftConsiderDuration(
      Boolean(
        showPracticeAttendanceChoice &&
          slot.consider_duration &&
          practiceWindowAllowsDuration(start, end)
      )
    );
  };

  const openAttendance = async (slot: TestSlot): Promise<boolean> => {
    setBusySlotId(slot.slot_id);
    setError("");
    try {
      const res = await authService.post(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slot.slot_id}/attendance`
      );
      const sessionId = res.data?.attendance_session_id as string | undefined;
      if (!sessionId) {
        throw new Error("No attendance session returned");
      }
      const firstBatch = slot.batches[0];
      const courseLabel =
        firstBatch?.course_name || firstBatch?.course_id || "Course";
      const courseId = firstBatch?.course_id || undefined;
      const batchLabels = slot.batches.map(
        (b) => b.batch_name || b.batch_id
      );
      const batchIds = slot.batches.map((b) => b.batch_id);
      const totalStudents = Number(
        res.data?.total_students ?? slot.assigned_student_count ?? 0
      );
      const purposeDetail =
        (res.data?.purpose_detail as string | undefined) ||
        `${testName || "Test"} - ${slot.label}`;
      const attendanceRoundId = res.data?.attendance_round_id as
        | string
        | undefined;
      const hasPriorRounds = Boolean(res.data?.has_prior_rounds);
      const hasActiveRound = Boolean(res.data?.has_active_round);
      const liveState: TrainerLiveState = {
        setupMode: "test",
        course: courseLabel,
        courseId,
        batches: batchLabels,
        batchIds,
        purposeDetail,
        eventType: testType || "Final Test",
        dateTime: new Date().toLocaleString(),
        totalStudents,
        groupSizes: [],
        groupCount: 0,
        attendanceSessionId: sessionId,
        ...(attendanceRoundId ? { attendanceRoundId } : {}),
        hasPriorRounds,
        hasActiveRound,
        // No reopen_slot — Return to Test should land on configure, not reopen the slot modal.
        returnToTestPath: routes.configureDetail(
          routeTestName || testName || "Test",
          testId
        ),
      };
      navigate("/admin/attendance/live", { state: liveState });
      return true;
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err as Error)?.message ||
        "Failed to start attendance.";
      setError(String(detail));
      return false;
    } finally {
      setBusySlotId(null);
    }
  };

  const requestAttendance = (slot: TestSlot) => {
    if (showPracticeAttendanceChoice) {
      if (isPracticeAttendanceDecisionLocked(slot)) {
        if (slot.consider_attendance) {
          void openAttendance(slot);
        }
        return;
      }
      setAttendanceChoiceSlotId(slot.slot_id);
      return;
    }
    void openAttendance(slot);
  };

  const skipPracticeAttendance = async () => {
    if (attendanceChoiceSlotId == null) return;
    const slot = slots.find((s) => s.slot_id === attendanceChoiceSlotId);
    if (slot && isPracticeAttendanceDecisionLocked(slot)) {
      setError(
        "Cannot change the attendance decision after the test has started."
      );
      return;
    }
    const slotId = attendanceChoiceSlotId;
    const ok = await patchSlot(slotId, { consider_attendance: false });
    if (ok) setAttendanceChoiceSlotId(null);
  };

  const takePracticeAttendance = async () => {
    const slot = slots.find((s) => s.slot_id === attendanceChoiceSlotId);
    if (
      slot &&
      isPracticeAttendanceDecisionLocked(slot) &&
      !slot.consider_attendance
    ) {
      setError(
        "Cannot change the attendance decision after the test has started."
      );
      return;
    }
    if (!slot) return;
    const ok = await openAttendance(slot);
    if (ok) setAttendanceChoiceSlotId(null);
  };

  const startSlotTest = async (slot: TestSlot) => {
    setBusySlotId(slot.slot_id);
    setError("");
    try {
      await authService.post(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slot.slot_id}/start`
      );
      await loadSlots();
      onSlotsChanged?.();
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to start slot.";
      setError(String(detail));
    } finally {
      setBusySlotId(null);
    }
  };

  const openSecurity = (slot: TestSlot) => {
    setSecuritySlotId(slot.slot_id);
    setDraftAutoRelease(slot.auto_release_test_report);
  };

  const saveSchedule = async () => {
    if (!scheduleSlotId) return;
    const durationMins = scheduleSlot?.duration_minutes || 0;

    if (showAttendanceStart) {
      if (!draftStart) {
        setError("Start time is required to schedule this slot.");
        return;
      }
      if (!(durationMins > 0)) {
        setError("Generate questions first so slot duration is known.");
        return;
      }
    } else {
      if (!draftStart || !draftEnd) {
        setError(
          "Start and end are required. Without an end schedule, the test is not visible to students."
        );
        return;
      }
      const startMs = new Date(draftStart).getTime();
      const endMs = new Date(draftEnd).getTime();
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
        setError("Enter a valid start and end date/time.");
        return;
      }
      if (startMs >= endMs) {
        setError("Start must be before end.");
        return;
      }
    }

    const endValue =
      showAttendanceStart && draftStart
        ? addMinutesToLocalInput(draftStart, durationMins)
        : draftEnd;
    const hasFullWindow = Boolean(draftStart && endValue);
    const scheduleLocked =
      showPracticeAttendanceChoice &&
      scheduleSlot != null &&
      isPracticeAttendanceDecisionLocked(scheduleSlot);
    const durationEligible =
      showPracticeAttendanceChoice &&
      practiceWindowAllowsDuration(draftStart, endValue);
    const ok = await patchSlot(scheduleSlotId, {
      ...(scheduleLocked ? {} : { start_time: fromLocalInput(draftStart) }),
      end_time: fromLocalInput(endValue),
      status: hasFullWindow ? "scheduled" : "draft",
      ...(showPracticeAttendanceChoice
        ? { consider_duration: durationEligible && draftConsiderDuration }
        : {}),
    });
    if (ok) setScheduleSlotId(null);
  };

  const saveSecurity = async () => {
    if (!securitySlotId) return;
    const slot = slots.find((s) => s.slot_id === securitySlotId);
    const ok = await patchSlot(securitySlotId, {
      auto_release_test_report: draftAutoRelease,
      report_released: draftAutoRelease
        ? false
        : Boolean(slot?.report_released),
    });
    if (ok) setSecuritySlotId(null);
  };

  const reviewSlot = slots.find((s) => s.slot_id === reviewSlotId);
  const scheduleSlot = slots.find((s) => s.slot_id === scheduleSlotId);
  const attendanceChoiceSlot = slots.find(
    (s) => s.slot_id === attendanceChoiceSlotId
  );
  const securitySlot = slots.find((s) => s.slot_id === securitySlotId);
  const reviewSwapLocked =
    reviewSlot?.status === "live" ||
    reviewSlot?.status === "completed" ||
    (reviewSlot?.assigned_student_count || 0) > 0;

  const reviewSwapLockReason =
    reviewSlot?.status === "live" || reviewSlot?.status === "completed"
      ? `Cannot change questions after the test has started or completed`
      : (reviewSlot?.assigned_student_count || 0) > 0
        ? "Cannot change questions after students are mapped to this slot"
        : "";

  const swapAvailableCount = questions.filter(
    (q) => (q.swap_available ?? 0) > 0
  ).length;
  const noSwapCount = questions.length - swapAvailableCount;

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold small text-secondary">
          Slots for this test ({slots.length})
        </div>
        <Button size="sm" variant="outline-primary" onClick={createSlot}>
          <LuPlus className="me-1" /> Add slot
        </Button>
      </div>
      {error && (
        <Alert variant="danger" className="py-2 small" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <PulseLoader color="#0d6efd" size={10} />
        </div>
      ) : slots.length === 0 ? (
        <div className="border rounded bg-light p-4 text-center text-muted small mb-3">
          No slots yet. Click <strong>Add Batch</strong> above to create a slot,
          or use <strong>Add slot</strong> for more schedules.
        </div>
      ) : (
        slots.map((slot) => {
          const expanded = expandedSlotId === slot.slot_id;
          const isScheduled = Boolean(slot.start_time);
          const isLive = slot.status === "live";
          const batchLockReason = getSlotBatchLockReason(slot);
          const deleteLockReason = getSlotDeleteLockReason(slot);
          const questionsGenerated = slot.question_count > 0;
          const questionsConfirmed =
            slot.status === "questions_ready" ||
            slot.status === "scheduled" ||
            slot.status === "live" ||
            slot.status === "completed";

          return (
            <div
              key={slot.slot_id}
              className="border rounded bg-white mb-3 shadow-sm"
            >
              <div className="px-3 py-2 bg-light border-bottom">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <button
                    type="button"
                    className="flex-grow-1 border-0 bg-transparent text-start p-0"
                    onClick={() =>
                      setExpandedSlotId(expanded ? null : slot.slot_id)
                    }
                    aria-expanded={expanded}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <span className="text-secondary mt-1">
                        {expanded ? <BsChevronUp /> : <BsChevronDown />}
                      </span>
                      <div>
                        <div className="fw-semibold text-dark">
                          {slot.label}
                          {slot.batches.length > 1
                            ? " · shared questions"
                            : isLive
                              ? " · live"
                              : isScheduled
                              ? " · scheduled slot"
                              : " · not scheduled"}
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-3 text-secondary small mt-1">
                          <span className="d-inline-flex align-items-center gap-1">
                            <BsCalendar3 />
                            {toDateLabel(slot.start_time)}
                          </span>
                          <span className="d-inline-flex align-items-center gap-1">
                            <BsClock />
                            {toTimeLabel(slot.start_time)}
                          </span>
                          {questionsConfirmed && (
                            <span className="text-success">ready</span>
                          )}
                          <span>{slot.duration_minutes} min</span>
                        </div>
                      </div>
                    </div>
                  </button>
                  <div className="d-flex flex-wrap align-items-center gap-2 flex-shrink-0">
                    <div className="d-flex gap-1">
                      <Button
                        variant={
                          questionsGenerated
                            ? "success"
                            : logicSaved
                              ? "outline-primary"
                              : "outline-secondary"
                        }
                        size="sm"
                        className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                        title={
                          questionsGenerated
                            ? "Questions saved for this slot — click to regenerate"
                            : logicSaved
                              ? "Generate questions from saved logic"
                              : "Save selection logic at test level first"
                        }
                        disabled={!logicSaved || busySlotId === slot.slot_id || isLive || slot.status === "completed"}
                        onClick={() => {
                          if (questionsGenerated) {
                            setRegeneratePromptSlotId(slot.slot_id);
                          } else {
                            generate(slot.slot_id, false);
                          }
                        }}
                      >
                        <BsShuffle />
                      </Button>
                      <Button
                        variant={
                          slot.auto_release_test_report
                            ? "success"
                            : "outline-secondary"
                        }
                        size="sm"
                        className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                        title="Report release settings"
                        onClick={() => openSecurity(slot)}
                      >
                        <BsShieldCheck />
                      </Button>
                      <Button
                        variant={isScheduled ? "success" : "outline-secondary"}
                        size="sm"
                        className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                        title={
                          isLive || slot.status === "completed"
                            ? "Schedule is locked after Start"
                            : showAttendanceStart
                              ? "Optional preview schedule (Start sets the live window)"
                              : "Schedule test"
                        }
                        disabled={isLive || slot.status === "completed"}
                        onClick={() => openSchedule(slot)}
                      >
                        <BsClock />
                      </Button>
                      {showAttendanceButton && (
                        <Button
                          variant={
                            showPracticeAttendanceChoice
                              ? slot.consider_attendance &&
                                slot.attendance_launched
                                ? "success"
                                : "outline-primary"
                              : slot.attendance_launched
                                ? "success"
                                : "outline-primary"
                          }
                          size="sm"
                          className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                          title={
                            (slot.assigned_student_count || 0) <= 0
                              ? "Assign students to this slot first"
                              : showPracticeAttendanceChoice &&
                                  isPracticeAttendanceDecisionLocked(slot)
                                ? slot.consider_attendance
                                  ? "Attendance decision is locked — open existing attendance"
                                  : "Attendance decision is locked; the test has started"
                                : showPracticeAttendanceChoice
                                  ? slot.consider_attendance
                                    ? "Attendance is being considered — click to take attendance or skip"
                                    : "Optional attendance — take it or don’t consider it"
                                  : slot.attendance_launched
                                    ? "Open existing attendance for this slot"
                                    : "Take attendance for this slot"
                          }
                          disabled={
                            busySlotId === slot.slot_id ||
                            (slot.assigned_student_count || 0) <= 0 ||
                            (!showPracticeAttendanceChoice &&
                              (slot.status === "live" ||
                                slot.status === "completed")) ||
                            (showPracticeAttendanceChoice &&
                              isPracticeAttendanceDecisionLocked(slot) &&
                              !slot.consider_attendance)
                          }
                          onClick={() => requestAttendance(slot)}
                        >
                          <MdOutlineFactCheck />
                        </Button>
                      )}
                      {showAttendanceStart && (
                          <Button
                            variant={
                              slot.status === "live" ||
                              slot.status === "completed"
                                ? "success"
                                : "outline-success"
                            }
                            size="sm"
                            className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                            title={
                              slot.status === "live" ||
                              slot.status === "completed"
                                ? "Slot already started"
                                : (slot.question_count || 0) <= 0
                                  ? "Generate questions for this slot first"
                                  : slot.attendance_round_completed
                                    ? "Start test for this slot"
                                    : "Finish attendance round before starting"
                            }
                            disabled={
                              busySlotId === slot.slot_id ||
                              slot.status === "live" ||
                              slot.status === "completed" ||
                              !slot.attendance_round_completed ||
                              (slot.question_count || 0) <= 0
                            }
                            onClick={() => startSlotTest(slot)}
                          >
                            <FaPlay />
                          </Button>
                      )}
                      <span
                        className="d-inline-flex"
                        title={deleteLockReason || `Delete ${slot.label}`}
                        style={
                          deleteLockReason
                            ? { cursor: "not-allowed" }
                            : undefined
                        }
                      >
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                          disabled={
                            busySlotId === slot.slot_id ||
                            Boolean(deleteLockReason)
                          }
                          style={
                            deleteLockReason
                              ? { pointerEvents: "none" }
                              : undefined
                          }
                          onClick={() =>
                            requestDeleteSlot(slot.slot_id, slot.label)
                          }
                        >
                          <BsTrash />
                        </Button>
                      </span>
                    </div>
                    <Badge bg="primary">
                      {slot.batches.length} batch(es)
                    </Badge>
                    {questionsGenerated && (
                      <Badge
                        bg="success"
                        role="button"
                        tabIndex={0}
                        onClick={() => openReview(slot.slot_id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openReview(slot.slot_id);
                          }
                        }}
                        title="Review generated questions"
                      >
                        {slot.question_count} Q
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {expanded && (
                <div className="p-0" ref={bodyRef}>
                  {slot.batches.length === 0 ? (
                    <div className="text-muted small text-center py-4 px-3">
                      No batches in this slot yet. Use{" "}
                      <strong>Add Batch</strong> above
                      {slots.length > 1
                        ? " while this slot is open"
                        : ""}
                      .
                    </div>
                  ) : (
                    slot.batches.map((slotBatch) => {
                      const cfg = resolveBatchConfig(slotBatch.batch_id);
                      const batchKey = String(slotBatch.batch_id);
                      const apiCount = Number(
                        slotBatch.assigned_student_count ??
                          slot.batch_assigned_counts?.[batchKey] ??
                          slot.batch_assigned_counts?.[slotBatch.batch_id] ??
                          0
                      );
                      // Slot-level total is always returned; use when this is
                      // the only batch on the slot (per-batch field used to be stripped).
                      const slotTotalFallback =
                        slot.batches.length === 1
                          ? Number(slot.assigned_student_count ?? 0)
                          : 0;
                      const slotCountKey = String(slot.slot_id);
                      const localSlotCount = Number(
                        cfg?.slot_assigned_counts?.[slotCountKey] ?? 0
                      );
                      // cfg.assignedStudentIds merges all slots for a batch —
                      // only safe when this batch is linked to a single slot.
                      const batchLinkedSlotCount = slots.filter((s) =>
                        s.batches.some(
                          (b) => String(b.batch_id) === batchKey
                        )
                      ).length;
                      const localCount =
                        batchLinkedSlotCount <= 1
                          ? Math.max(
                              cfg?.assignedStudentIds?.length ?? 0,
                              localSlotCount
                            )
                          : localSlotCount;
                      const studentCount = Math.max(
                        apiCount,
                        slotTotalFallback,
                        localCount
                      );
                      return (
                        <div
                          key={slotBatch.batch_id}
                          className="d-flex justify-content-between align-items-center gap-2 py-2 px-3 border-top"
                        >
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-semibold">
                              {slotBatch.batch_name ||
                                cfg?.batch_name ||
                                slotBatch.batch_id}
                            </span>
                            <Badge bg="light" text="dark" className="border">
                              {slotBatch.course_name ||
                                cfg?.course_name ||
                                "—"}
                            </Badge>
                          </div>
                          <div className="d-flex gap-1 flex-shrink-0">
                            <Button
                              variant={
                                studentCount > 0
                                  ? "success"
                                  : "outline-primary"
                              }
                              size="sm"
                              className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                              title={
                                studentCount > 0
                                  ? `${studentCount} student(s) assigned`
                                  : "Assign students"
                              }
                              disabled={busySlotId === slot.slot_id}
                              onClick={() => {
                                const entry: BatchConfigEntry =
                                  cfg ??
                                  ({
                                    batch_id: slotBatch.batch_id,
                                    batch_name:
                                      slotBatch.batch_name || slotBatch.batch_id,
                                    course_id: String(slotBatch.course_id || ""),
                                    course_name: slotBatch.course_name || "",
                                    track_id: "",
                                    track_name: "",
                                    subjects: [],
                                    test_type: null,
                                    date: null,
                                    time: null,
                                    securityConfig: {
                                      ...DEFAULT_TEST_SECURITY_CONFIGURATION,
                                    },
                                    status: "draft",
                                    assignedStudentIds: [],
                                    slot_assigned_counts: {},
                                  } as BatchConfigEntry);
                                onAssignStudents(entry, slot.slot_id);
                              }}
                            >
                              <FaUsers />
                            </Button>
                            <span
                              className="d-inline-flex"
                              title={batchLockReason || "Remove batch"}
                              style={
                                batchLockReason
                                  ? { cursor: "not-allowed" }
                                  : undefined
                              }
                            >
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
                                disabled={
                                  busySlotId === slot.slot_id ||
                                  Boolean(batchLockReason)
                                }
                                style={
                                  batchLockReason
                                    ? { pointerEvents: "none" }
                                    : undefined
                                }
                                onClick={() =>
                                  requestDeleteBatch(
                                    slot.slot_id,
                                    slotBatch.batch_id,
                                    slotBatch.batch_name ||
                                      cfg?.batch_name ||
                                      slotBatch.batch_id
                                  )
                                }
                              >
                                <BsX size={16} />
                              </Button>
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Schedule modal */}
      <Modal
        show={scheduleSlotId != null}
        onHide={() => setScheduleSlotId(null)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Schedule — {scheduleSlot?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className={showAttendanceStart ? "mb-0" : "mb-3"}>
            <Form.Label className="small fw-semibold">Start</Form.Label>
            <Form.Control
              type="datetime-local"
              value={draftStart}
              disabled={
                showPracticeAttendanceChoice &&
                scheduleSlot != null &&
                isPracticeAttendanceDecisionLocked(scheduleSlot)
              }
              onChange={(e) => {
                const next = e.target.value;
                setDraftStart(next);
                if (showAttendanceStart) {
                  setDraftEnd(
                    addMinutesToLocalInput(
                      next,
                      scheduleSlot?.duration_minutes || 0
                    )
                  );
                }
              }}
            />
            {showPracticeAttendanceChoice &&
              scheduleSlot != null &&
              isPracticeAttendanceDecisionLocked(scheduleSlot) && (
                <p className="small text-muted mb-0 mt-2">
                  Start is locked because the test has already started.
                </p>
              )}
          </Form.Group>
          {showAttendanceStart ? (
            <p className="small text-muted mb-0 mt-2">
              {scheduleSlot?.duration_minutes
                ? draftStart
                  ? `End (auto): ${toDateLabel(
                      fromLocalInput(draftEnd)
                    )} ${toTimeLabel(fromLocalInput(draftEnd))} · ${
                      scheduleSlot.duration_minutes
                    } min duration`
                  : `End will be Start + ${scheduleSlot.duration_minutes} min`
                : "Generate questions first so slot duration is known; End is set automatically."}
            </p>
          ) : (
            <Form.Group>
              <Form.Label className="small fw-semibold">End</Form.Label>
              <Form.Control
                type="datetime-local"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                required
              />
            </Form.Group>
          )}
          {showPracticeAttendanceChoice && (
            <Form.Group className="mt-3">
              <Form.Check
                type="checkbox"
                id="practice-consider-duration"
                label="Show duration timer"
                checked={
                  draftConsiderDuration &&
                  practiceWindowAllowsDuration(draftStart, draftEnd)
                }
                disabled={!practiceWindowAllowsDuration(draftStart, draftEnd)}
                onChange={(e) => setDraftConsiderDuration(e.target.checked)}
              />
              <p className="small text-muted mb-0 mt-1">
                {practiceWindowAllowsDuration(draftStart, draftEnd)
                  ? "Students see remaining window time as a countdown instead of start and end."
                  : "Available only when the slot window is less than 24 hours."}
              </p>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setScheduleSlotId(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveSchedule}
            disabled={
              showAttendanceStart
                ? !(
                    draftStart &&
                    scheduleSlot?.duration_minutes &&
                    scheduleSlot.duration_minutes > 0
                  )
                : !(draftStart && draftEnd)
            }
          >
            Save schedule
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={attendanceChoiceSlotId != null}
        onHide={() => setAttendanceChoiceSlotId(null)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Attendance — {attendanceChoiceSlot?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Attendance is optional for Practice Tests. Take it now to allow
            only present students to start, or don&apos;t consider it so all
            assigned students can take the test.
          </p>
          {attendanceChoiceSlot &&
          isPracticeAttendanceDecisionLocked(attendanceChoiceSlot) ? (
            <p className="small text-warning mb-0">
              The test has started. The attendance decision can no longer be
              changed.
            </p>
          ) : attendanceChoiceSlot?.consider_attendance ? (
            <p className="small text-muted mb-0">
              Attendance is currently being considered. Only students marked
              present can start.
            </p>
          ) : attendanceChoiceSlot?.attendance_session_id ? (
            <p className="small text-muted mb-0">
              Attendance is not being considered. All assigned students can
              take the test during the scheduled window.
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setAttendanceChoiceSlotId(null)}
          >
            Cancel
          </Button>
          {!(
            attendanceChoiceSlot &&
            isPracticeAttendanceDecisionLocked(attendanceChoiceSlot)
          ) && (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => void skipPracticeAttendance()}
                disabled={busySlotId === attendanceChoiceSlotId}
              >
                Don&apos;t consider attendance
              </Button>
              <Button
                variant="primary"
                onClick={() => void takePracticeAttendance()}
                disabled={busySlotId === attendanceChoiceSlotId}
              >
                Take attendance
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Report release modal */}
      <Modal
        show={securitySlotId != null}
        onHide={() => setSecuritySlotId(null)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Report release — {securitySlot?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Check
            type="checkbox"
            id="slot-auto-release"
            label="Auto-release test report after slot end"
            checked={draftAutoRelease}
            onChange={(e) => setDraftAutoRelease(e.target.checked)}
          />
          <p className="text-muted small mt-2 mb-0">
            These settings apply to this slot only. Students see the report when
            auto-release unlocks after the slot ends.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSecuritySlotId(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveSecurity}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Generated questions review — matches new-test-flow modal */}
      <Modal
        show={reviewSlotId != null}
        onHide={() => setReviewSlotId(null)}
        size="xl"
        centered
        scrollable
        backdrop="static"
        keyboard={false}
        dialogClassName="generated-questions-modal"
        className="test-flow-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Generated Questions — {reviewSlot?.label || testName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="bg-light rounded p-3 mb-3">
            <div className="fw-semibold small mb-1">
              Shared for {reviewSlot?.batches.length || 0} batch(es)
            </div>
            <div className="text-muted small">
              {(reviewSlot?.batches || [])
                .map((b) => b.batch_name || b.batch_id)
                .join(" · ") || "No batches"}
            </div>
          </div>

          {reviewSwapLocked && (
            <Alert variant="warning" className="mb-2 py-2 small">
              {(reviewSlot?.assigned_student_count || 0) > 0
                ? "Students are mapped to this slot. Select / Swap are disabled."
                : `This slot is ${reviewSlot?.status}. Select / Swap are disabled.`}
            </Alert>
          )}
          {swapNotice && (
            <Alert
              variant="info"
              className="py-2 small"
              dismissible
              onClose={() => setSwapNotice(null)}
            >
              {swapNotice}
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert variant="warning" className="py-2 small">
              {warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </Alert>
          )}

          {reviewLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : questions.length === 0 ? (
            <div className="text-muted small text-center py-4">
              No questions generated yet.
            </div>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Badge bg="success">
                  {swapAvailableCount} row(s) — swap available
                </Badge>
                {noSwapCount > 0 && (
                  <Badge bg="secondary">
                    {noSwapCount} row(s) — no swap
                  </Badge>
                )}
              </div>
              <Table responsive size="sm" className="mb-0 border">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "4rem" }}>Sl. No.</th>
                    <th>Level</th>
                    <th>Topic / Subtopic</th>
                    <th>Question</th>
                    <th>Type</th>
                    <th className="text-end text-nowrap" style={{ width: "1%", minWidth: "9.5rem" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question, index) => {
                    const canSwap = (question.swap_available ?? 0) > 0;
                    const isSwapping =
                      swappingQuestionId === question.question_id;
                    const swapDisabled =
                      !canSwap ||
                      reviewSwapLocked ||
                      isSwapping ||
                      swappingAll ||
                      Boolean(swappingQuestionId);
                    const swapTooltip = reviewSwapLocked
                      ? reviewSwapLockReason
                      : canSwap
                        ? "Pick a random replacement from the question bank"
                        : "No swap available";
                    const openPreview = () =>
                      setPreviewQuestion({
                        question_id: question.question_id,
                        question_type: question.question_type,
                        question_types_enum: question.question_types_enum,
                        level: question.level,
                        subject_name: question.subject_name,
                        topic_name: question.topic_name,
                        sub_topic_name: question.sub_topic_name,
                        question_data: question.question_data || null,
                      });
                    return (
                      <tr
                        key={question.question_id}
                        className={!canSwap ? "table-light" : undefined}
                      >
                        <td className="fw-semibold">
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none fw-semibold"
                            title="Preview question"
                            onClick={openPreview}
                          >
                            {index + 1}
                          </button>
                        </td>
                        <td>
                          <Badge bg="secondary">
                            {(question.level || "").replace("level", "L")}
                          </Badge>
                        </td>
                        <td className="small">
                          {question.topic_name}
                          <div className="text-muted">
                            {question.sub_topic_name}
                          </div>
                        </td>
                        <td className="small">
                          <div className="d-flex align-items-start gap-2">
                            <span
                              className="text-primary flex-grow-1"
                              role="button"
                              title="Preview question"
                              onClick={openPreview}
                            >
                              {questionText(question)}
                            </span>
                            <QuestionUsedBadge question={question} />
                          </div>
                        </td>
                        <td>{question.question_type}</td>
                        <td
                          className="text-end text-nowrap"
                          style={{ width: "1%", minWidth: "9.5rem" }}
                        >
                          <div className="d-inline-flex flex-nowrap gap-1 justify-content-end align-items-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              title={
                                reviewSwapLocked
                                  ? reviewSwapLockReason
                                  : "Browse and pick a replacement from this subtopic"
                              }
                              disabled={
                                reviewSwapLocked ||
                                isSwapping ||
                                swappingAll ||
                                Boolean(swappingQuestionId) ||
                                !routeTestName
                              }
                              onClick={() => openSelectReplacement(question)}
                            >
                              Select
                            </Button>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`swap-tip-${question.question_id}`}>
                                  {swapTooltip}
                                </Tooltip>
                              }
                            >
                              <span
                                className="d-inline-block"
                                style={
                                  swapDisabled
                                    ? { cursor: "not-allowed" }
                                    : undefined
                                }
                              >
                                <Button
                                  variant={
                                    canSwap && !reviewSwapLocked
                                      ? "outline-primary"
                                      : "outline-secondary"
                                  }
                                  size="sm"
                                  disabled={swapDisabled}
                                  className={
                                    swapDisabled ? "opacity-50 pe-none" : undefined
                                  }
                                  onClick={() =>
                                    swap(question.question_id, false)
                                  }
                                >
                                  {isSwapping ? (
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-1"
                                    />
                                  ) : null}
                                  Swap
                                </Button>
                              </span>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="flex-wrap">
          <Button variant="secondary" onClick={() => setReviewSlotId(null)}>
            Close
          </Button>
          <Button
            variant="outline-primary"
            disabled={
              reviewSwapLocked ||
              questions.length === 0 ||
              swappingAll ||
              Boolean(swappingQuestionId)
            }
            title={
              reviewSwapLocked ? reviewSwapLockReason : undefined
            }
            onClick={() => swap(undefined, true)}
          >
            {swappingAll ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Swap all
          </Button>
          <Button
            variant="success"
            disabled={questions.length === 0}
            onClick={() => {
              setReviewSlotId(null);
              void loadSlots();
            }}
          >
            Done ({questions.length} questions)
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={regeneratePromptSlotId != null}
        onHide={() => setRegeneratePromptSlotId(null)}
        onConfirm={() => {
          const slotId = regeneratePromptSlotId;
          setRegeneratePromptSlotId(null);
          if (slotId != null) {
            void generate(slotId, true);
          }
        }}
        onCancel={() => {
          const slotId = regeneratePromptSlotId;
          setRegeneratePromptSlotId(null);
          if (slotId != null) {
            void openReview(slotId);
          }
        }}
        title="Questions already generated"
        message="Do you want to regenerate or check the existing question?"
        confirmText="Regenerate"
        cancelText="Existing"
        variant="warning"
      />

      <ConfirmationModal
        show={deleteTarget != null}
        onHide={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={
          deleteTarget?.kind === "batch" ? "Delete batch" : "Delete slot"
        }
        message={
          deleteTarget?.kind === "slot"
            ? `Are you sure you want to delete this slot ${deleteTarget.name}?`
            : deleteTarget?.kind === "batch"
              ? `Are you sure you want to delete this batch ${deleteTarget.name}?`
              : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />

      <QuestionPreviewModal
        question={previewQuestion}
        onHide={() => setPreviewQuestion(null)}
      />
    </div>
  );
});

TestSlotsPanel.displayName = "TestSlotsPanel";

export default TestSlotsPanel;
